import { Text } from '@chakra-ui/react'
import {
  ContentNode,
  ContentNodeUtils,
  FieldContentNode,
  ObjectContentNode,
} from '@helo/cms'
import { Deferred, pipe } from '@helo/utils'

import { Skeleton } from '../components/SkeletonLoader'
import { WidgetRenderer2 } from '../widgets/WidgetRenderer/WidgetRenderer2'
import { IDRenderer } from './ID/IDRenderer'
import { StringRenderer } from './String/StringRenderer'
import { TypeRendererParts } from './TypeRenderer.parts'
import { BooleanRenderer } from './boolean/BooleanRenderer'
import { DateRenderer } from './date/DateRenderer'
import { ListRenderer } from './list/ListRenderer'
import { NullRenderer } from './null/NullRenderer'
import { NumberRenderer } from './number/NumberRenderer'
import { TagRenderer } from './tag/TagRenderer'

// GraphQL types from supergraph that have both tagName and tagValue fields and end with 'Tag'
const TAG_TYPE_NAMES = [
  'CatalogItemPurchaseOrderTag',
  'CatalogItemTag',
  'PurchaseOrderCiPoTag',
  'ShipmentDocumentCiPoTag',
  'ShipmentDocumentFulfillmentItemTag',
  'ShipmentDocumentFulfillmentItemOrderTag',
  'ShipmentDocumentTag',
  'TransportUnitCiPoTag',
  'TransportUnitClusterTag',
  'TransportUnitClusterCiPoTag',
  'TransportUnitShipmentDocumentTag',
]

interface TypeRendererProps {
  contentNode: ContentNode
  isLoading?: boolean
}

export const TypeRenderer = ({ contentNode, isLoading }: TypeRendererProps) => {
  if (!contentNode) {
    return <NullRenderer isLoading={isLoading} />
  }
  if (!contentNode) {
    console.error('no contentNode', { contentNode })
    return null
  }

  if (contentNode.state.asWidget) {
    const { asWidget, ...restState } = contentNode.state
    const contentNodeWithoutAsWidget = {
      ...contentNode,
      state: restState,
    }
    const widgetConfig = {
      ...(asWidget as any),
      state: {
        ...((asWidget as any).state || {}),
        widgetProps: {
          contentNode: contentNodeWithoutAsWidget,
          ...(restState.widgetOptions?.[asWidget?.type] || {}),
          ...((asWidget as any).state?.widgetProps || {}),
        },
      },
    }

    return (
      <WidgetRenderer2
        widgetConfig={widgetConfig}
        customPayload={{
          isLoading,
        }}
      />
    )
  }

  if (contentNode.kind === 'SCALAR') {
    if (contentNode.typeName === 'String') {
      return <StringRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Int') {
      return <NumberRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Float') {
      return <NumberRenderer contentNode={contentNode} isLoading={isLoading} />
    }

    if (
      contentNode.typeName === 'DateTime' ||
      contentNode.typeName === 'Date'
    ) {
      return <DateRenderer contentNode={contentNode} isLoading={isLoading} />
    }

    if (contentNode.typeName === 'ID') {
      return <IDRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Boolean') {
      return <BooleanRenderer contentNode={contentNode} isLoading={isLoading} />
    }
  }
  if (contentNode.kind === 'FIELD') {
    const ofTypeWithState = pipe(
      contentNode,
      ContentNodeUtils.getOfType(),
      Deferred.merge({
        state: pipe(
          contentNode.state ?? {},
          Deferred.merge(contentNode.ofType.state ?? {}),
        ),
      }),
    ) as ContentNode
    return <TypeRenderer contentNode={ofTypeWithState} isLoading={isLoading} />
  }
  if (contentNode.kind === 'LIST') {
    return <ListRenderer contentNode={contentNode} isLoading={isLoading} />
  }

  if (TAG_TYPE_NAMES.includes(contentNode.typeName)) {
    return (
      <TagRenderer
        contentNode={contentNode as ObjectContentNode}
        isLoading={isLoading}
      />
    )
  }
  if (contentNode.kind === 'OBJECT') {
    const primary = TypeRendererParts.withPrimaryValue(contentNode)
    if (primary !== contentNode) {
      return <TypeRenderer contentNode={primary} isLoading={isLoading} />
    }
    return <ObjectRenderer contentNode={contentNode} isLoading={isLoading} />
  }

  console.error('unknown type', { contentNode })
  return '-'
}

interface ObjectRendererProps {
  contentNode: ObjectContentNode
  isLoading?: boolean
}

const ObjectRenderer = ({ contentNode, isLoading }: ObjectRendererProps) => {
  if (contentNode.kind !== 'OBJECT') {
    console.error('not an object', { contentNode })
    return null
  }

  const fields = contentNode.fields.map(fieldConfig => {
    return (
      <FieldRenderer
        key={fieldConfig.key}
        contentNode={fieldConfig}
        isLoading={isLoading}
      />
    )
  })

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        rowGap: 0,
        columnGap: '4px',
      }}
    >
      {/* <Text fontWeight="bold" fontSize="30px">
        {contentNode.state.label as any}
      </Text> */}
      {fields}
    </div>
  )
}

interface FieldRendererProps {
  contentNode: FieldContentNode
  isLoading?: boolean
}

const FieldRenderer = ({ contentNode, isLoading }: FieldRendererProps) => {
  const isFieldPending = TypeRendererParts.isValuePending(
    contentNode.ofType,
    isLoading,
  )

  return (
    <>
      <Skeleton isLoading={isFieldPending} h="14px" w="56px" my="1px">
        <Text fontSize="14px" lineHeight="17px" fontWeight="500">
          {contentNode.state.label as string}:
        </Text>
      </Skeleton>

      <TypeRenderer
        contentNode={contentNode.ofType}
        isLoading={isFieldPending}
      />
    </>
  )
}
